import React, { useState } from 'react';
import { useAPR } from '../context/APRContext';
import { Application } from '../types';

const ApplicationScoring: React.FC = () => {
  const { state, dispatch } = useAPR();
  const [selectedStatus, setSelectedStatus] = useState<'All' | 'Completed' | 'Pending'>('All');

  // Get applications that are in "In-Process" APR runs
  const inProcessAPRRuns = state.aprRuns.filter(run => run.status === 'In-Process');
  const inProcessApplicationIds = new Set(
    inProcessAPRRuns.flatMap(run => run.selectedApplicationIds || [])
  );

  // Filter applications: only show those in "In-Process" APR runs
  // IMPORTANT: Exclude parent applications - only child applications can be scored
  const inProcessApplications = state.applications.filter(app =>
    inProcessApplicationIds.has(app.id) && !app.isParent
  );

  // Count parent applications that were excluded
  const excludedParentCount = state.applications.filter(app =>
    inProcessApplicationIds.has(app.id) && app.isParent
  ).length;

  // Then apply additional filters based on Q&A status
  const filteredApplications = inProcessApplications.filter(app => {
    const statusMatch = selectedStatus === 'All' || 
      (selectedStatus === 'Completed' && false) || // TODO: Will implement Q&A completed logic later
      (selectedStatus === 'Pending' && true); // For now, all are pending
    
    return statusMatch;
  });


  return (
    <div className="md-page">
      {/* Material Design App Bar */}
      <div className="md-app-bar">
        <div className="md-app-bar__content">
          <div className="md-app-bar__title-section">
            <h1 className="md-app-bar__title">Application Scoring (4R)</h1>
            <p className="md-app-bar__subtitle">
              Review and analyze scores for applications currently in "In-Process" status
            </p>
          </div>
          <div className="md-app-bar__actions">
            {/* Q&A Status Filter */}
            <div className="md-filter-section">
              <label htmlFor="statusFilter" className="md-filter-label">Filter by Q&A Status</label>
              <select
                id="statusFilter"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as 'All' | 'Completed' | 'Pending')}
                className="md-select"
              >
                <option value="All">All Applications</option>
                <option value="Completed">Q&A Completed</option>
                <option value="Pending">Q&A Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Material Design Cards Layout */}
      <div className="md-content">
        {/* In-Process APR Runs Info */}
        {inProcessAPRRuns.length > 0 && (
          <div className="md-card md-card--outlined">
            <div className="md-card__content">
              <h3 className="md-card__title">Current In-Process APR Runs</h3>
              <div className="apr-runs-info">
                {inProcessAPRRuns.map(run => (
                  <div key={run.id} className="apr-run-badge">
                    <span className="apr-run-badge__name">{run.rationalizationName}</span>
                    <span className="apr-run-badge__count">
                      {(run.selectedApplicationIds || []).length} apps
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Notice about excluded parent applications */}
        {excludedParentCount > 0 && (
          <div className="md-card md-card--outlined" style={{ borderColor: '#f59e0b', backgroundColor: '#fffbeb' }}>
            <div className="md-card__content" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e' }}>
                <span style={{ fontSize: '1.2rem' }}>ℹ️</span>
                <span>
                  <strong>{excludedParentCount} parent application{excludedParentCount > 1 ? 's' : ''}</strong> excluded from scoring.
                  Only child applications are eligible for Q&A assessment and scoring.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="md-stats-grid">
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">{inProcessApplications.length}</div>
                <div className="md-stat__label">In-Process Applications</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {inProcessApplications.filter(app => app.applicationOwner || app.technicalOwnerCustodian).length}
                </div>
                <div className="md-stat__label">With Assigned Owners</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  0
                </div>
                <div className="md-stat__label">Q&A Completed</div>
              </div>
            </div>
          </div>
          
          <div className="md-card md-card--filled">
            <div className="md-card__content">
              <div className="md-stat">
                <div className="md-stat__value">
                  {inProcessApplications.length}
                </div>
                <div className="md-stat__label">Q&A Pending</div>
              </div>
            </div>
          </div>
        </div>

        {/* Applications Display */}
        {filteredApplications.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">🎯</div>
            <h3 className="md-empty-state__title">No In-Process Applications Found</h3>
            <p className="md-empty-state__description">
              {inProcessApplications.length === 0 
                ? 'No applications are currently in "In-Process" status. Create an APR run and set its status to "In-Process" to see applications here.'
                : selectedStatus !== 'All'
                  ? 'No in-process applications match the selected Q&A status filter.'
                  : 'No in-process applications available for Q&A.'
              }
            </p>
          </div>
        ) : (
          <div className="scoring-table-container">
            <div className="scoring-table">
              <div className="scoring-table__header">
                <div className="scoring-table__cell scoring-table__cell--header">Application</div>
                <div className="scoring-table__cell scoring-table__cell--header">Technology</div>
                <div className="scoring-table__cell scoring-table__cell--header">Users</div>
                <div className="scoring-table__cell scoring-table__cell--header">Application Owners</div>
                <div className="scoring-table__cell scoring-table__cell--header">Q&A Status</div>
                <div className="scoring-table__cell scoring-table__cell--header">Business Domain</div>
              </div>
              
              {filteredApplications.map(app => (
                <div key={app.id} className="scoring-table__row">
                  <div className="scoring-table__cell">
                    <div className="app-info">
                      <div className="app-info__name">{app.name}</div>
                      {app.applicationIdCode && (
                        <div className="app-info__id">ID: {app.applicationIdCode}</div>
                      )}
                    </div>
                  </div>
                  <div className="scoring-table__cell">
                    {app.technologyStack || 'Not specified'}
                  </div>
                  <div className="scoring-table__cell">
                    {app.userCount?.toLocaleString() || 'Unknown'}
                  </div>
                  <div className="scoring-table__cell">
                    <div className="owners-info">
                      {app.applicationOwner && (
                        <div className="owner-primary">
                          <span className="owner-name">{app.applicationOwner}</span>
                          {app.appOwnerEmail && (
                            <span className="owner-email">{app.appOwnerEmail}</span>
                          )}
                        </div>
                      )}
                      {app.technicalOwnerCustodian && app.technicalOwnerCustodian !== app.applicationOwner && (
                        <div className="owner-technical">
                          <span className="owner-label">Tech:</span>
                          <span className="owner-name">{app.technicalOwnerCustodian}</span>
                          {app.toEmail && (
                            <span className="owner-email">{app.toEmail}</span>
                          )}
                        </div>
                      )}
                      {!app.applicationOwner && !app.technicalOwnerCustodian && (
                        <span className="owners-none">Not specified</span>
                      )}
                    </div>
                  </div>
                  <div className="scoring-table__cell">
                    <div className="qa-status">
                      <span className="qa-status-chip qa-status-chip--pending">
                        <span className="qa-status-chip__icon">⏳</span>
                        <span className="qa-status-chip__text">Pending</span>
                      </span>
                    </div>
                  </div>
                  <div className="scoring-table__cell">
                    {app.businessFunctionDomain || 'Not specified'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationScoring;